/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuotePaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const txRef = searchParams.get('tx_ref');
    const transactionId = searchParams.get('transaction_id');
    const status = searchParams.get('status');
    
    if (!txRef) {
      setStatus('error');
      setError('Missing payment reference');
      return;
    }

    // Extract quote_id from tx_ref (format: QUOTE_{quote_number}_{timestamp})
    const txRefParts = txRef.split('_');
    if (txRefParts.length < 2 || txRefParts[0] !== 'QUOTE') {
      setStatus('error');
      setError('Invalid payment reference format');
      return;
    }

    // Try to verify the payment
    verifyPayment(txRef, transactionId);
  }, [searchParams]);

  const verifyPayment = async (txRef, transactionId) => {
    try {
      // Get payment record to find quote_id
      const paymentResponse = await fetch(`${API_URL}/api/payments/flutterwave/status/${txRef}`);
      
      if (!paymentResponse.ok) {
        throw new Error('Failed to fetch payment status');
      }
      
      const paymentInfo = await paymentResponse.json();
      const quoteId = paymentInfo.quote_id;
      
      if (!quoteId) {
        // Payment found but not a quote payment, show generic success
        if (paymentInfo.status === 'successful') {
          setStatus('success');
          setPaymentData(paymentInfo);
          return;
        }
        throw new Error('Quote information not found');
      }

      // Verify quote payment
      const verifyResponse = await fetch(`${API_URL}/api/quote/pay/${quoteId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: transactionId,
          tx_ref: txRef
        })
      });
      
      const result = await verifyResponse.json();
      
      if (result.status && (result.data?.status === 'successful' || result.data?.status === 'already_paid')) {
        setStatus('success');
        setPaymentData({
          tx_ref: txRef,
          ...result.data
        });
      } else {
        setStatus('error');
        setError(result.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setStatus('error');
      setError(err.message || 'Unable to verify payment. Please contact support.');
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-600">Verifying your payment...</p>
          <p className="text-sm text-zinc-400 mt-2">Please wait, do not close this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-zinc-50">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          {status === 'success' ? (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={48} />
              </div>
              <h1 className="text-3xl font-bold text-green-600 mb-4" data-testid="payment-success">
                Payment Successful!
              </h1>
              <p className="text-zinc-600 mb-6">
                Thank you for your payment. Your order is now being processed.
              </p>
              {paymentData && (
                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-zinc-500 mb-1">Transaction Reference</p>
                  <p className="font-mono text-sm mb-3">{paymentData.tx_ref}</p>
                  {paymentData.payment_reference && (
                    <>
                      <p className="text-sm text-zinc-500 mb-1">Payment Reference</p>
                      <p className="font-mono text-sm">{paymentData.payment_reference}</p>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-zinc-500 mb-6">
                <Receipt size={16} />
                <span>A receipt has been sent to your email</span>
              </div>
              <button
                onClick={() => window.location.href = '/'}
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition w-full"
                data-testid="go-home-btn"
              >
                Return to Home
              </button>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="text-red-600" size={48} />
              </div>
              <h1 className="text-3xl font-bold text-red-600 mb-4" data-testid="payment-failed">
                Payment Issue
              </h1>
              <p className="text-zinc-600 mb-6">
                {error || 'We encountered an issue verifying your payment.'}
              </p>
              <p className="text-sm text-zinc-500 mb-6">
                If you were charged, please contact support with your transaction details. We'll resolve this promptly.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = 'mailto:temarucoltd@gmail.com?subject=Quote Payment Issue'}
                  className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition w-full"
                  data-testid="contact-support-btn"
                >
                  Contact Support
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="border border-zinc-300 text-zinc-700 px-8 py-3 rounded-lg font-semibold hover:bg-zinc-50 transition w-full"
                  data-testid="try-again-btn"
                >
                  Go Back
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-zinc-500">
          <p>© {new Date().getFullYear()} Temaruco Clothing Factory</p>
        </div>
      </div>
    </div>
  );
};

export default QuotePaymentCallbackPage;
